import { useCallback, useEffect, useState } from "react";

import { listAiReputationReports } from "../api/aiReputation";
import { listReports as listPerceptionReports } from "../api/perception";
import { listReports } from "../api/persona";
import { listVisibilityReports } from "../api/visibility";
import { api } from "../utils/apiClient";

export const useReportLists = (user: unknown) => {
  const [reports, setReports] = useState({
    audits: [],
    visibility: [],
    ota: [],
    persona: [],
    reputation: [],
    perception: [],
  });
  const [loading, setLoading] = useState(false);
  const refresh = useCallback(async () => {
    if (!user) {
      setReports({
        audits: [],
        visibility: [],
        ota: [],
        persona: [],
        reputation: [],
        perception: [],
      });
      return;
    }
    setLoading(true);
    const requests = [
      api.get("/user/audits"),
      listVisibilityReports(),
      api.get("/user/ota-reports"),
      listReports(),
      listAiReputationReports(),
      listPerceptionReports(),
    ];
    const settled = await Promise.allSettled(requests);
    const values = settled.map((result) =>
      result.status === "fulfilled" ? (result.value.data ?? []) : [],
    );
    setReports({
      audits: values[0],
      visibility: values[1],
      ota: values[2],
      persona: values[3],
      reputation: values[4],
      perception: values[5],
    });
    setLoading(false);
  }, [user]);
  useEffect(() => {
    const timer = window.setTimeout(refresh, 0);
    return () => window.clearTimeout(timer);
  }, [refresh]);
  useEffect(() => {
    window.addEventListener("personaReportsUpdated", refresh);
    window.addEventListener("aiReputationReportsUpdated", refresh);
    window.addEventListener("perceptionReportsUpdated", refresh);
    return () => {
      window.removeEventListener("personaReportsUpdated", refresh);
      window.removeEventListener("aiReputationReportsUpdated", refresh);
      window.removeEventListener("perceptionReportsUpdated", refresh);
    };
  }, [refresh]);
  return { reports, loading, refresh };
};

/** Keys of the report map returned by {@link useReportLists}, one per report type. */
export type ReportKey = keyof ReturnType<typeof useReportLists>["reports"];
